import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useState } from "react";

const FONT_SIZE = "0.875rem";
const LABEL_SX = { fontSize: FONT_SIZE };

const fmt = (val, dp = 4) => (val != null ? numbers.dpString(dp)(val) : "—");
const fmtCurrency = (val) =>
  val != null ? numbers.currencyFormat(val, 0) : "—";

const buildSectionRow = (row, type) => {
  const exposureUnits = row.exposure_amount / (row.basis_divisor || 1000);
  const lcm = row.lcm ?? 1;
  const terr = row.territory_factor ?? 1;

  if (type === "prem_ops") {
    const basicLc = exposureUnits * (row.prem_loss_cost || 0) * lcm * terr;
    const netIlf = Math.max(
      0,
      (row.prem_ilf || 0) - (row.prem_ded_factor || 0)
    );
    return {
      state: row.state,
      class_code: row.class_code,
      description: row.description,
      exposure_base: row.basis_unit_label,
      exposure_amount: row.exposure_amount,
      loss_rate: row.prem_loss_cost,
      rating_basis: row.prem_rating_basis,
      override_type: "prem_ops",
      basic_limits_loss_cost: basicLc,
      ilf_factor: netIlf,
      selected_loss_cost: row.prem_ops_premium,
    };
  }
  if (type === "prod_cops") {
    const basicLc = exposureUnits * (row.pco_loss_cost || 0) * lcm;
    const netIlf = Math.max(0, (row.pco_ilf || 0) - (row.pco_ded_factor || 0));
    return {
      state: row.state,
      class_code: row.class_code,
      description: row.description,
      exposure_base: row.basis_unit_label,
      exposure_amount: row.exposure_amount,
      loss_rate: row.pco_loss_cost,
      rating_basis: row.pco_rating_basis,
      override_type: "prod_cops",
      basic_limits_loss_cost: basicLc,
      ilf_factor: netIlf,
      selected_loss_cost: row.pco_premium,
    };
  }
  if (type === "liquor") {
    const sf = row.liquor_state_factor ?? 1;
    const basicLc = exposureUnits * (row.pco_loss_cost || 0) * lcm * sf;
    return {
      state: row.state,
      class_code: row.class_code,
      description: row.description,
      exposure_base: row.basis_unit_label,
      exposure_amount: row.exposure_amount,
      countrywide_elp: row.pco_loss_cost,
      basic_limits_loss_cost: basicLc,
      selected_loss_cost: row.liquor_premium,
    };
  }
  return null;
};

const adaptResponse = (values) => {
  if (!values?.records?.length) return null;
  const sum = (arr, key) => arr.reduce((a, r) => a + (r[key] || 0), 0);

  const prem_ops_rows = [];
  const prod_cops_rows = [];
  const liquor_rows = [];

  for (const row of values.records) {
    if (row.ilf_table_prem && row.ilf_table_prem !== "-") {
      prem_ops_rows.push(buildSectionRow(row, "prem_ops"));
    }
    if (row.ilf_table_pco && row.ilf_table_pco !== "-") {
      prod_cops_rows.push(buildSectionRow(row, "prod_cops"));
    }
    if (row.is_liquor_class) {
      liquor_rows.push(buildSectionRow(row, "liquor"));
    }
  }

  const blendedIlf = (rows) => {
    const t = sum(rows, "selected_loss_cost");
    const b = sum(rows, "basic_limits_loss_cost");
    return b ? t / b : 0;
  };

  return {
    prem_ops: {
      records: prem_ops_rows,
      basic_limits_total: sum(prem_ops_rows, "basic_limits_loss_cost"),
      blended_ilf: blendedIlf(prem_ops_rows),
      total: sum(prem_ops_rows, "selected_loss_cost"),
    },
    prod_cops: {
      records: prod_cops_rows,
      basic_limits_total: sum(prod_cops_rows, "basic_limits_loss_cost"),
      blended_ilf: blendedIlf(prod_cops_rows),
      total: sum(prod_cops_rows, "selected_loss_cost"),
    },
    liquor: {
      records: liquor_rows,
      total: sum(liquor_rows, "selected_loss_cost"),
    },
    totals: values.totals ?? {},
    total_premium: values.totals?.gl_total,
  };
};

const SectionHeader = ({ label, total, open, onToggle }) => (
  <TableRow
    sx={{ backgroundColor: "grey.100", cursor: "pointer" }}
    onClick={onToggle}
  >
    <TableCell colSpan={9}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography
          fontSize={FONT_SIZE}
          fontWeight={700}
          color={"text.secondary"}
        >
          {label}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} spacing={1}>
          {total != null && (
            <Typography
              fontSize={FONT_SIZE}
              fontWeight={600}
              color={"text.secondary"}
            >
              {numbers.currencyFormat(total, 0)}
            </Typography>
          )}
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          )}
        </Stack>
      </Stack>
    </TableCell>
  </TableRow>
);

const CollapsibleSection = ({ open, children }) => (
  <TableRow sx={{ p: 0 }}>
    <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Table size={"small"} sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>{children}</TableBody>
        </Table>
      </Collapse>
    </TableCell>
  </TableRow>
);

const ExposureHead = ({ isLiquor }) => (
  <TableRow sx={{ backgroundColor: "grey.50" }}>
    <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 60 }}>
      {"State"}
    </TableCell>
    <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 90 }}>
      {"Class Code"}
    </TableCell>
    <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600 }}>
      {"Description"}
    </TableCell>
    <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 130 }}>
      {"Exposure Base"}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 110 }}
      align={"right"}
    >
      {"Exposure Amt"}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 110 }}
      align={"right"}
    >
      {isLiquor ? "CW ELP" : "Loss Rate"}
    </TableCell>
    <TableCell
      sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 100 }}
      align={"right"}
    >
      {"Selected LC"}
    </TableCell>
  </TableRow>
);

// Loss-cost rating bases the underwriter may override on GL Pricing (ELP/RTC).
const OVERRIDABLE_RATING_BASES = new Set(["ELP", "RTC"]);

const LossRateCell = ({ row, isLiquor, overrides, onOverride }) => {
  const canOverride =
    !isLiquor && !!onOverride && OVERRIDABLE_RATING_BASES.has(row.rating_basis);
  if (!canOverride) {
    return fmt(isLiquor ? row.countrywide_elp : row.loss_rate);
  }
  const key = `${row.state}${row.class_code}`;
  // Defaults to the published rate ($0 for RTC); the override wins once typed. The
  // tooltip explains why the rate is editable (ELP estimate / RTC refer-to-company).
  const value = overrides?.[key] ?? row.loss_rate ?? null;
  return (
    <Tooltip
      title={
        row.rating_basis === "RTC"
          ? "Refer to company (RTC) — published rate $0; enter a loss rate"
          : "Estimated Loss Potential (ELP) — published rate is an estimate; may be overridden"
      }
      placement={"top"}
    >
      <PrettyNumberTextField
        hiddenLabel
        fullWidth
        size={"small"}
        variant={"filled"}
        value={value}
        onChangeNumber={(v) => onOverride(key, v)}
        inputProps={{ style: { textAlign: "right", padding: "2px 6px" } }}
      />
    </Tooltip>
  );
};

const ExposureRow = ({ row, isLiquor, overrides, onOverride }) => (
  <TableRow hover>
    <TableCell sx={LABEL_SX}>{row.state}</TableCell>
    <TableCell sx={LABEL_SX}>{row.class_code}</TableCell>
    <TableCell
      sx={{
        ...LABEL_SX,
        maxWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Tooltip title={row.description ?? ""} placement={"top"}>
        <span>{row.description}</span>
      </Tooltip>
    </TableCell>
    <TableCell sx={{ fontSize: FONT_SIZE, color: "text.secondary" }}>
      {row.exposure_base}
    </TableCell>
    <TableCell sx={LABEL_SX} align={"right"}>
      {fmtCurrency(row.exposure_amount)}
    </TableCell>
    <TableCell sx={LABEL_SX} align={"right"}>
      <LossRateCell
        row={row}
        isLiquor={isLiquor}
        overrides={overrides}
        onOverride={onOverride}
      />
    </TableCell>
    <TableCell sx={{ ...LABEL_SX, fontWeight: 600 }} align={"right"}>
      {fmtCurrency(row.selected_loss_cost)}
    </TableCell>
  </TableRow>
);

const TotalsRows = ({ section }) => (
  <>
    <TableRow sx={{ backgroundColor: "grey.50" }}>
      <TableCell colSpan={6} sx={{ fontSize: FONT_SIZE, fontWeight: 700 }}>
        {"Total Selected Loss Cost"}
      </TableCell>
      <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 700 }} align={"right"}>
        {fmtCurrency(section.total)}
      </TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={7} sx={{ border: 0, py: 0.75 }} />
    </TableRow>
  </>
);

const CoverageSection = ({
  label,
  section,
  isLiquor = false,
  overrides,
  onOverride,
  open,
  onToggle,
}) => {
  if (!section?.records?.length) return null;
  return (
    <>
      <SectionHeader
        label={label}
        total={section.total}
        open={open}
        onToggle={onToggle}
      />
      <CollapsibleSection open={open}>
        <ExposureHead isLiquor={isLiquor} />
        {section.records.map((row, i) => (
          <ExposureRow
            key={i}
            row={row}
            isLiquor={isLiquor}
            overrides={overrides}
            onOverride={onOverride}
          />
        ))}
        <TotalsRows section={section} />
      </CollapsibleSection>
    </>
  );
};

const CascadeRow = ({ label, value, bold, color }) => (
  <TableRow>
    <TableCell
      colSpan={7}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 400,
        color: color ?? (bold ? "text.primary" : "text.secondary"),
        border: 0,
        py: 0.5,
      }}
    >
      {label}
    </TableCell>
    <TableCell
      colSpan={2}
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: bold ? 700 : 600,
        color: color ?? "text.primary",
        border: 0,
        py: 0.5,
      }}
    >
      {value}
    </TableCell>
  </TableRow>
);

const fmtPct = (val) =>
  val != null && !Number.isNaN(val) ? `${(val * 100).toFixed(2)}%` : "—";

const SummaryTotalRow = ({ label, value }) => (
  <TableRow sx={{ backgroundColor: "grey.50" }}>
    <TableCell colSpan={7} sx={{ fontSize: FONT_SIZE, fontWeight: 700 }}>
      {label}
    </TableCell>
    <TableCell
      colSpan={2}
      sx={{ fontSize: FONT_SIZE, fontWeight: 700 }}
      align={"right"}
    >
      {value}
    </TableCell>
  </TableRow>
);

const CascadeInputRow = ({ label, value, onChange }) => (
  <TableRow>
    <TableCell
      colSpan={7}
      align={"right"}
      sx={{ fontSize: FONT_SIZE, color: "text.secondary", border: 0, py: 0.5 }}
    >
      {label}
    </TableCell>
    <TableCell colSpan={2} sx={{ border: 0, py: 0.25 }} align={"right"}>
      <PrettyNumberTextField
        hiddenLabel
        size={"small"}
        variant={"filled"}
        value={value}
        onChangeNumber={onChange}
        sx={{
          width: "100%",
          "& .MuiInputBase-input": {
            fontSize: FONT_SIZE,
            textAlign: "right",
            py: "4px",
            px: 1,
          },
        }}
      />
    </TableCell>
  </TableRow>
);

const SubSectionHeader = ({ label, total, open, onToggle }) => (
  <TableRow
    sx={{ backgroundColor: "grey.50", cursor: "pointer" }}
    onClick={onToggle}
  >
    <TableCell colSpan={9} sx={{ py: 0.75 }}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          )}
          <Typography
            fontSize={"0.8rem"}
            fontWeight={600}
            color={"text.secondary"}
          >
            {label}
          </Typography>
        </Stack>
        {total != null && (
          <Typography fontSize={FONT_SIZE} fontWeight={700}>
            {numbers.currencyFormat(total, 0)}
          </Typography>
        )}
      </Stack>
    </TableCell>
  </TableRow>
);

// Editable PremOps territory factors. One row per unique state+class; the
// permissible range is calculated by the engine (tlbPO_Proxy) and the factor is
// an underwriter override clamped to it. Locked rows (min == max) are read-only.
const TerritoryFactorsSection = ({
  records,
  territoryFactors,
  onUpdate,
  open,
  onToggle,
}) => {
  const seen = new Set();
  const rows = [];
  for (const r of records ?? []) {
    if (!r.ilf_table_prem || r.ilf_table_prem === "-") continue;
    const key = `${r.state}${r.class_code}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(r);
  }
  if (!rows.length) return null;

  const setFactor = (key, min, max, v) => {
    const clamped = v == null ? null : Math.max(min, Math.min(max, v));
    onUpdate?.({ glTerritoryFactors: { ...territoryFactors, [key]: clamped } });
  };

  return (
    <>
      <SectionHeader
        label={"PremOps Territory Factors"}
        open={open}
        onToggle={onToggle}
      />
      <CollapsibleSection open={open}>
        <TableRow sx={{ backgroundColor: "grey.50" }}>
          <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 60 }}>
            {"State"}
          </TableCell>
          <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 90 }}>
            {"Class Code"}
          </TableCell>
          <TableCell sx={{ fontSize: FONT_SIZE, fontWeight: 600 }}>
            {"Description"}
          </TableCell>
          <TableCell
            sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 130 }}
            align={"right"}
          >
            {"Permissible Range"}
          </TableCell>
          <TableCell
            sx={{ fontSize: FONT_SIZE, fontWeight: 600, width: 110 }}
            align={"right"}
          >
            {"Terr. Factor"}
          </TableCell>
        </TableRow>
        {rows.map((r) => {
          const key = `${r.state}${r.class_code}`;
          const min = r.terr_factor_min ?? 1;
          const max = r.terr_factor_max ?? 1;
          const locked = Math.abs(max - min) < 1e-9;
          const value = territoryFactors?.[key] ?? r.territory_factor ?? 1;
          return (
            <TableRow key={key} hover>
              <TableCell sx={LABEL_SX}>{r.state}</TableCell>
              <TableCell sx={LABEL_SX}>{r.class_code}</TableCell>
              <TableCell
                sx={{
                  ...LABEL_SX,
                  maxWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={r.description ?? ""} placement={"top"}>
                  <span>{r.description}</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{ ...LABEL_SX, color: "text.secondary" }}
                align={"right"}
              >
                {`${fmt(min, 2)} – ${fmt(max, 2)}`}
              </TableCell>
              <TableCell align={"right"} sx={{ py: 0.25 }}>
                <PrettyNumberTextField
                  hiddenLabel
                  size={"small"}
                  variant={"filled"}
                  value={value}
                  disabled={locked}
                  onChangeNumber={(v) => setFactor(key, min, max, v)}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-input": {
                      fontSize: FONT_SIZE,
                      textAlign: "right",
                      py: "4px",
                      px: 1,
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </CollapsibleSection>
    </>
  );
};

const GlPricingResults = ({
  values,
  territoryFactors = {},
  premOpsLcOverrides = {},
  pcoLcOverrides = {},
  onUpdate,
}) => {
  const [open, setOpen] = useState({
    territory: true,
    prem_ops: true,
    prod_cops: true,
    liquor: true,
    summary: true,
    summary_tech: true,
    summary_adj: true,
    summary_composite: true,
  });

  const data = adaptResponse(values);
  if (!data) return null;

  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  // ELP/RTC loss-cost overrides (keyed by `${state}${class_code}`); null reverts to
  // the published rate. Only ELP/RTC rows expose the input (see LossRateCell).
  const onPremOpsOverride = (key, value) =>
    onUpdate?.({
      glPremOpsLossCostOverrides: { ...premOpsLcOverrides, [key]: value },
    });
  const onPcoOverride = (key, value) =>
    onUpdate?.({
      glPcoLossCostOverrides: { ...pcoLcOverrides, [key]: value },
    });

  const t = data.totals;

  const hasCovAdj =
    (t?.coverage_enhancements_premium ?? 0) !== 0 ||
    (t?.stop_gap_premium ?? 0) !== 0;

  const totalPremium =
    t?.charged_premium_with_tria ??
    t?.charged_premium ??
    t?.total_cgl ??
    data.total_premium;

  return (
    <TableContainer>
      <Table size={"small"} sx={{ tableLayout: "fixed" }}>
        <TableBody>
          <TerritoryFactorsSection
            records={values?.records}
            territoryFactors={territoryFactors}
            onUpdate={onUpdate}
            open={open.territory}
            onToggle={() => toggle("territory")}
          />
          <CoverageSection
            label={"Premises / Operations"}
            section={data.prem_ops}
            overrides={premOpsLcOverrides}
            onOverride={onPremOpsOverride}
            open={open.prem_ops}
            onToggle={() => toggle("prem_ops")}
          />
          <CoverageSection
            label={"Products / Completed Operations"}
            section={data.prod_cops}
            overrides={pcoLcOverrides}
            onOverride={onPcoOverride}
            open={open.prod_cops}
            onToggle={() => toggle("prod_cops")}
          />
          {data.liquor?.records?.length > 0 && (
            <CoverageSection
              label={"Liquor Liability"}
              section={data.liquor}
              isLiquor
              open={open.liquor}
              onToggle={() => toggle("liquor")}
            />
          )}

          <SectionHeader
            label={"Premium Summary"}
            total={totalPremium}
            open={open.summary}
            onToggle={() => toggle("summary")}
          />
          <CollapsibleSection open={open.summary}>
            <SubSectionHeader
              label={(() => {
                const parts = [];
                if (
                  t?.pro_rata_adjustment != null &&
                  t.pro_rata_adjustment !== 100
                ) {
                  parts.push(
                    `${numbers.dpString(3)(t.pro_rata_adjustment)}% pro rata`
                  );
                }
                if (t?.insured_interest) parts.push(t.insured_interest);
                return parts.length
                  ? `Technical Build-up (${parts.join(" · ")})`
                  : "Technical Build-up";
              })()}
              total={t?.total_cgl}
              open={open.summary_tech}
              onToggle={() => toggle("summary_tech")}
            />
            <CollapsibleSection open={open.summary_tech}>
              <CascadeRow
                label={"Technical PremOps"}
                value={fmtCurrency(t?.prem_ops_base)}
              />
              {(t?.coverage_enhancements_premium ?? 0) !== 0 && (
                <CascadeRow
                  label={`  + Coverage Enhancements (${fmtPct(
                    t.coverage_enhancements_pct
                  )})`}
                  value={fmtCurrency(t.coverage_enhancements_premium)}
                />
              )}
              {(t?.stop_gap_premium ?? 0) !== 0 && (
                <CascadeRow
                  label={"  + Stop Gap"}
                  value={fmtCurrency(t.stop_gap_premium)}
                />
              )}
              {hasCovAdj && (
                <CascadeRow
                  label={"Technical PremOps (incl. add-ons)"}
                  value={fmtCurrency(t?.prem_ops_technical)}
                />
              )}
              <CascadeRow
                label={"Products / Completed Operations"}
                value={fmtCurrency(t?.pco)}
              />
              {(t?.liquor ?? 0) > 0 && (
                <CascadeRow
                  label={"Liquor Liability"}
                  value={fmtCurrency(t.liquor)}
                />
              )}
              {(t?.hnoa_premium ?? 0) > 0 && (
                <CascadeRow
                  label={"HNOA"}
                  value={fmtCurrency(t.hnoa_premium)}
                />
              )}
              {t?.apply_experience_mod && (t?.experience_mod ?? 0) !== 0 && (
                <CascadeRow
                  label={
                    "Experience Mod (applied to PremOps / PCO / Liquor above)"
                  }
                  value={fmtPct(t.experience_mod)}
                  color={t.experience_mod < 0 ? "success.main" : "warning.main"}
                />
              )}
              <SummaryTotalRow
                label={"Total CGL"}
                value={fmtCurrency(t?.total_cgl)}
              />
            </CollapsibleSection>

            <SubSectionHeader
              label={"Adjustments"}
              total={t?.charged_premium}
              open={open.summary_adj}
              onToggle={() => toggle("summary_adj")}
            />
            <CollapsibleSection open={open.summary_adj}>
              <CascadeRow
                label={`IRPM Modifier (${fmtPct(t?.irpm_modifier ?? 0)})`}
                value={fmtCurrency(
                  (t?.guideline_premium ?? 0) - (t?.total_cgl ?? 0)
                )}
              />
              <CascadeRow
                label={"Guideline Premium"}
                value={fmtCurrency(t?.guideline_premium)}
                bold
              />
              {(t?.manual_premium_adjustment ?? 0) !== 0 && (
                <CascadeRow
                  label={"Manual Premium Adjustment"}
                  value={fmtCurrency(t.manual_premium_adjustment)}
                  color={
                    t.manual_premium_adjustment < 0
                      ? "warning.main"
                      : "text.primary"
                  }
                />
              )}
              <CascadeInputRow
                label={"Charged to Technical (%)"}
                value={
                  t?.charged_to_technical != null
                    ? t.charged_to_technical * 100
                    : null
                }
                onChange={(v) => {
                  if (v == null || !onUpdate) return;
                  const newAdj =
                    (t?.total_cgl ?? 0) * (v / 100) -
                    (t?.guideline_premium ?? 0);
                  onUpdate({ manualPremiumAdjustment: newAdj });
                }}
              />
              <CascadeInputRow
                label={"Charged Premium ($)"}
                value={t?.charged_premium ?? null}
                onChange={(v) => {
                  if (v == null || !onUpdate) return;
                  onUpdate({
                    manualPremiumAdjustment: v - (t?.guideline_premium ?? 0),
                  });
                }}
              />
            </CollapsibleSection>

            {(t?.tria_percent ?? 0) > 0 && (
              <>
                <CascadeRow
                  label={`TRIA (${fmtPct(t.tria_percent)})`}
                  value={fmtCurrency(t.tria_premium)}
                />
                <CascadeRow
                  label={"Charged Premium incl. TRIA"}
                  value={fmtCurrency(t.charged_premium_with_tria)}
                  bold
                />
              </>
            )}
            {(t?.commission_pct ?? 0) > 0 && (
              <CascadeRow
                label={`Commission (${fmtPct(t.commission_pct)})`}
                value={fmtCurrency(t.commission_amount)}
                color={"text.secondary"}
              />
            )}

            {((t?.total_exposure ?? 0) > 0 ||
              (t?.implied_composite_rate ?? 0) > 0) && (
              <>
                <SubSectionHeader
                  label={"Composite Rate"}
                  open={open.summary_composite}
                  onToggle={() => toggle("summary_composite")}
                />
                <CollapsibleSection open={open.summary_composite}>
                  {(t?.total_exposure ?? 0) > 0 && (
                    <CascadeRow
                      label={"Total Composite Exposure"}
                      value={fmtCurrency(t.total_exposure)}
                      color={"text.secondary"}
                    />
                  )}
                  {(t?.implied_composite_rate ?? 0) > 0 && (
                    <CascadeRow
                      label={"Implied Composite Rate (excl. TRIA)"}
                      value={numbers.dpString(4)(t.implied_composite_rate)}
                      color={"text.secondary"}
                    />
                  )}
                </CollapsibleSection>
              </>
            )}

            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell
                colSpan={7}
                sx={{ fontSize: FONT_SIZE, fontWeight: 700 }}
              >
                {"Total Premium"}
              </TableCell>
              <TableCell
                colSpan={2}
                sx={{ fontSize: FONT_SIZE, fontWeight: 700 }}
                align={"right"}
              >
                {fmtCurrency(totalPremium)}
              </TableCell>
            </TableRow>
          </CollapsibleSection>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GlPricingResults;
